import { Link } from '@inertiajs/react';
import { ArrowUpRight, Sparkles, Clock, Star } from 'lucide-react';
import { motion } from 'motion/react';

import { SectionHeader } from '@/theme1/components/common/SectionHeader';

export default function CapsFeaturedSeries({ series, className = '' }) {
    // Safe extraction of series items
    let seriesItems = [];

    if (Array.isArray(series)) {
        // If series is directly an array
        seriesItems = series;
    } else if (series && typeof series === 'object') {
        // If series is an object, try to get items from various possible properties
        seriesItems = series.items || series.data || series.series || [];

        // If still empty and it's an object with numeric keys, convert to array
        if (!seriesItems.length && !Array.isArray(series)) {
            const values = Object.values(series);
            if (
                values.length &&
                values.some((v) => typeof v === 'object' && v !== null)
            ) {
                seriesItems = values;
            }
        }
    }

    // Ensure we have an array
    if (!Array.isArray(seriesItems)) {
        seriesItems = [];
    }

    if (!seriesItems.length) {
        return (
            <section className={`py-16 sm:py-20 lg:py-24 ${className}`}>
                <div className="container mx-auto px-4 text-center">
                    <p className="text-neutral-500">No series available</p>
                </div>
            </section>
        );
    }

    // Get section title and subtitle
    const sectionTitle = series?.title || 'Find your series.';
    const sectionSubtitle =
        series?.subtitle ||
        'Explore Captex silhouettes through performance, street, classic, minimal, and limited collections.';

    return (
        <section className={`py-16 sm:py-20 lg:py-24 ${className}`}>
            <div className="container mx-auto px-4">
                <SectionHeader
                    eyebrow="Shop by identity"
                    title={sectionTitle}
                    description={sectionSubtitle}
                    className="mb-10 sm:mb-12 lg:mb-14"
                />

                <div className="relative">
                    {/* Gradient fade indicators for scroll */}
                    <div className="pointer-events-none absolute top-0 right-0 z-10 h-full w-20 bg-gradient-to-l from-white to-transparent lg:hidden" />
                    <div className="pointer-events-none absolute top-0 left-0 z-10 h-full w-20 bg-gradient-to-r from-white to-transparent lg:hidden" />

                    <div className="flex snap-x [scrollbar-width:none] gap-4 overflow-x-auto pb-6 sm:gap-5 md:gap-6 [&::-webkit-scrollbar]:hidden">
                        {seriesItems.map((item, index) => (
                            <motion.div
                                key={item.id || index}
                                initial={{ opacity: 0, x: 24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: '-40px' }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.05,
                                }}
                                className="w-[85%] shrink-0 snap-start sm:w-[48%] lg:w-[30%] xl:w-[23%]"
                            >
                                <Link
                                    href={
                                        isSafeCollectionHref(item.href)
                                            ? item.href
                                            : safeCollectionHref(item)
                                    }
                                    className="group relative block min-h-[380px] overflow-hidden rounded-2xl bg-neutral-100 focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 focus:outline-none sm:min-h-[400px] md:min-h-[420px] lg:rounded-[2rem]"
                                >
                                    {/* Background Image */}
                                    <img
                                        src={item.image}
                                        alt={`${item.name || item.title} cap series`}
                                        loading="lazy"
                                        className="absolute inset-0 size-full object-cover transition duration-700 group-hover:scale-110"
                                    />

                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 transition duration-700 group-hover:from-black/95 group-hover:via-black/50" />

                                    {/* Badges */}
                                    <div className="absolute top-4 right-4 left-4 flex flex-wrap gap-2">
                                        {item.isNew && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-3 py-1 text-[10px] font-bold tracking-wider text-white uppercase backdrop-blur-sm">
                                                <Sparkles className="size-3" />
                                                New
                                            </span>
                                        )}
                                        {item.isLimited && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-3 py-1 text-[10px] font-bold tracking-wider text-white uppercase backdrop-blur-sm">
                                                <Clock className="size-3" />
                                                Limited
                                            </span>
                                        )}
                                        {item.isBestSeller && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/90 px-3 py-1 text-[10px] font-bold tracking-wider text-white uppercase backdrop-blur-sm">
                                                <Star className="size-3" />
                                                Best Seller
                                            </span>
                                        )}
                                        {item.count && (
                                            <span className="ml-auto inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-[10px] font-medium text-white backdrop-blur-sm">
                                                {item.count}{' '}
                                                {item.count === 1
                                                    ? 'Style'
                                                    : 'Styles'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-6 md:p-8">
                                        <div className="flex items-end justify-between gap-4">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-bold tracking-[0.2em] text-white/50 uppercase sm:text-xs">
                                                    {item.series ||
                                                        'Captex Series'}
                                                </p>

                                                <h3 className="mt-2 text-2xl font-black tracking-tight text-white uppercase sm:text-3xl md:text-4xl">
                                                    {item.name || item.title}
                                                </h3>

                                                {item.description && (
                                                    <p className="mt-2 line-clamp-2 max-w-xs text-sm leading-relaxed text-white/70 sm:text-base">
                                                        {item.description}
                                                    </p>
                                                )}

                                                {/* Color indicator if available */}
                                                {item.color && (
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <span
                                                            className="size-3 rounded-full ring-2 ring-white/30"
                                                            style={{
                                                                backgroundColor:
                                                                    item.color,
                                                            }}
                                                        />
                                                        <span className="text-xs text-white/50">
                                                            Signature color
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <motion.span
                                                whileHover={{ rotate: 45 }}
                                                transition={{ duration: 0.3 }}
                                                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-black shadow-lg transition-colors hover:bg-neutral-100 sm:size-12"
                                            >
                                                <ArrowUpRight className="size-4 sm:size-5" />
                                            </motion.span>
                                        </div>
                                    </div>

                                    {/* Hover overlay effect */}
                                    <div className="absolute inset-0 border-2 border-transparent transition-colors duration-300 group-hover:border-white/20 lg:rounded-[2rem]" />
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* View All Button */}
                <div className="mt-10 text-center sm:mt-12">
                    <Link
                        href="/collections"
                        className="group inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-6 py-3 text-sm font-medium text-neutral-700 transition-all duration-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white hover:shadow-lg sm:px-8 sm:py-4"
                    >
                        Explore All Collections
                        <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:rotate-45" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function safeCollectionHref(item) {
    const slug = item?.slug || item?.title || item?.name;
    const normalizedSlug = slugify(slug);
    return normalizedSlug ? `/collections/${normalizedSlug}` : '/collections';
}

function isSafeCollectionHref(href) {
    return (
        typeof href === 'string' &&
        href.trim() !== '' &&
        !href.toLowerCase().includes('undefined') &&
        !href.toLowerCase().includes('null')
    );
}

function slugify(value = '') {
    const normalized = String(value).trim();

    if (
        !normalized ||
        normalized.toLowerCase() === 'undefined' ||
        normalized.toLowerCase() === 'null'
    ) {
        return '';
    }

    return normalized
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
